import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@hp/ui";
import { updateDraftArgument, revertDraftArgument } from "../data/index";
import { diffWords } from "../lib/diffWords";
import { ErrorState } from "./Feedback";

/**
 * Turns `draft_argument` from a static, AI-generated string into a first
 * draft counsel can actually mark up: an editable textarea, a tracked-
 * changes view diffing the current text against `draft_argument_original`,
 * and a one-click revert back to exactly what the pipeline first wrote.
 */
export function DraftArgumentEditor({
  claimCandidateId,
  draftArgument,
  draftArgumentOriginal,
}: {
  claimCandidateId: string;
  draftArgument: string | null;
  draftArgumentOriginal: string | null;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = React.useState<"read" | "edit">("read");
  const [draft, setDraft] = React.useState(draftArgument ?? "");
  const [showDiff, setShowDiff] = React.useState(false);

  React.useEffect(() => {
    setDraft(draftArgument ?? "");
  }, [draftArgument]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["claim-candidates", claimCandidateId] });
  };

  const saveMutation = useMutation({
    mutationFn: (text: string) => updateDraftArgument(claimCandidateId, text),
    onSuccess: () => {
      setMode("read");
      invalidate();
    },
  });

  const revertMutation = useMutation({
    mutationFn: () => revertDraftArgument(claimCandidateId),
    onSuccess: () => {
      setMode("read");
      setShowDiff(false);
      invalidate();
    },
  });

  const hasEdits = draftArgumentOriginal !== null && draftArgument !== draftArgumentOriginal;

  if (!draftArgument) {
    return <p className="text-sm text-slate-400">No draft argument generated yet for this candidate.</p>;
  }

  if (mode === "edit") {
    return (
      <div className="flex flex-col gap-2">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={8}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        {saveMutation.isError ? <ErrorState error={saveMutation.error} label="Could not save your edits" /> : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setDraft(draftArgument);
              setMode("read");
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={saveMutation.isPending || draft.trim() === ""}
            onClick={() => saveMutation.mutate(draft)}
          >
            {saveMutation.isPending ? "Saving..." : "Save edits"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {showDiff && draftArgumentOriginal ? (
        <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">
          {diffWords(draftArgumentOriginal, draftArgument).map((segment, index) => {
            if (segment.type === "same") return <React.Fragment key={index}>{segment.text}</React.Fragment>;
            if (segment.type === "added") {
              return (
                <mark key={index} className="rounded bg-emerald-100 px-0.5 text-emerald-900">
                  {segment.text}
                </mark>
              );
            }
            return (
              <span key={index} className="rounded bg-red-100 px-0.5 text-red-700 line-through">
                {segment.text}
              </span>
            );
          })}
        </p>
      ) : (
        <p className="whitespace-pre-line text-sm text-slate-700">{draftArgument}</p>
      )}

      {revertMutation.isError ? <ErrorState error={revertMutation.error} label="Could not revert" /> : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" onClick={() => setMode("edit")}>
          Edit
        </Button>
        {hasEdits ? (
          <>
            <Button type="button" size="sm" variant="secondary" onClick={() => setShowDiff((prev) => !prev)}>
              {showDiff ? "Hide changes" : "Show changes vs. AI draft"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="danger"
              disabled={revertMutation.isPending}
              onClick={() => {
                if (confirm("Discard your edits and restore the original AI-generated draft?")) {
                  revertMutation.mutate();
                }
              }}
            >
              {revertMutation.isPending ? "Reverting..." : "Revert to AI draft"}
            </Button>
          </>
        ) : (
          <span className="text-xs text-slate-400">AI-generated - not yet edited</span>
        )}
      </div>
    </div>
  );
}
